import type { ErrorRegistryEntry } from './types';

export const ERROR_REGISTRY = {
  // ============ TIER 1: checkout blockers ============
  OFFER_EXPIRED: {
    code: 'OFFER_EXPIRED',
    httpStatus: 410,
    category: 'business_rule',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'This offer expired',
    defaultAction: 'Refresh the page to see current offers.',
  },
  OFFER_NOT_ASSIGNED: {
    code: 'OFFER_NOT_ASSIGNED',
    httpStatus: 403,
    category: 'auth',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: "This offer isn't available to you",
    defaultAction: 'Browse offers from your store homepage.',
  },
  MOQ_QUANTITY: {
    code: 'MOQ_QUANTITY',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Minimum order quantity not met',
    defaultAction: 'Add more units to meet the minimum.',
  },
  MOQ_AMOUNT: {
    code: 'MOQ_AMOUNT',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Minimum order amount not met',
    defaultAction: 'Add more items to meet the minimum amount.',
  },
  INVENTORY_INSUFFICIENT: {
    code: 'INVENTORY_INSUFFICIENT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Not enough inventory',
    defaultAction: 'Adjust the quantity in your cart.',
  },
  LOCATION_REQUIRED: {
    code: 'LOCATION_REQUIRED',
    httpStatus: 422,
    category: 'validation',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'Shipping address is required',
    defaultAction: 'Select or add a shipping address.',
  },
  LOCATION_NOT_FOUND: {
    code: 'LOCATION_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Shipping address not found',
    defaultAction: 'Pick a different address.',
  },
  LOCATION_NOT_OWNED: {
    code: 'LOCATION_NOT_OWNED',
    httpStatus: 403,
    category: 'auth',
    severity: 'error',
    retryable: false,
    capturePostHog: true,
    defaultMessage: "You can't ship to that address",
    defaultAction: 'Pick one of your saved addresses.',
  },
  INVALID_CUSTOMER_STATE: {
    code: 'INVALID_CUSTOMER_STATE',
    httpStatus: 422,
    category: 'validation',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'State must be a valid United States state',
    defaultAction: 'Use a two-letter USPS state abbreviation.',
  },
  BUNDLE_MIN_NOT_MET: {
    code: 'BUNDLE_MIN_NOT_MET',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Bundle requirement not met',
    defaultAction: 'Add more items from the required group.',
  },
  MOQ_NOT_MET: {
    code: 'MOQ_NOT_MET',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Minimum order quantity not met for this offer.',
    defaultAction: 'Add more units across your cart locations to meet the MOQ.',
  },
  OFFER_LOCKED: {
    code: 'OFFER_LOCKED',
    httpStatus: 409,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'This offer is locked because you have a pending order for it.',
    defaultAction: 'Finish submitting your remaining locations, or cancel the pending order.',
  },
  TAKEALL_MISMATCH: {
    code: 'TAKEALL_MISMATCH',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Take-all offer requires every item',
    defaultAction: 'Restore the offer from your cart to try again.',
  },
  SIZE_RUN_RATIO_MISMATCH: {
    code: 'SIZE_RUN_RATIO_MISMATCH',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage:
      'Size-run item quantities must be a positive integer multiple of the locked ratio and equal across members.',
    defaultAction: 'Adjust the size-run quantities and try again.',
  },
  SIZE_RUN_OVER_ALLOCATED: {
    code: 'SIZE_RUN_OVER_ALLOCATED',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Requested size-run quantity exceeds available allocation.',
    defaultAction: 'Reduce the size-run quantity and try again.',
  },
  OFFER_ITEM_OVER_ALLOCATED: {
    code: 'OFFER_ITEM_OVER_ALLOCATED',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Requested quantity exceeds the available allocation for this item.',
    defaultAction: 'Reduce the quantity for this item and try again.',
  },
  SIZE_RUN_INSUFFICIENT_STOCK: {
    code: 'SIZE_RUN_INSUFFICIENT_STOCK',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Requested runs exceed available inventory.',
    defaultAction: 'Reduce the number of runs or wait for restocking.',
  },
  SIZE_RUN_INCOMPLETE: {
    code: 'SIZE_RUN_INCOMPLETE',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Size-run submission must include all member variants.',
    defaultAction: 'Include every member variant in your size-run submission.',
  },
  SIZE_RUN_MEMBER_DIRECT_ADD_DISALLOWED: {
    code: 'SIZE_RUN_MEMBER_DIRECT_ADD_DISALLOWED',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage:
      'This variant belongs to a size-run bundle; add the bundle to your cart instead.',
    defaultAction: 'Use the size-run flow to add this item to your cart.',
  },
  STORE_ACCESS_DENIED: {
    code: 'STORE_ACCESS_DENIED',
    httpStatus: 403,
    category: 'auth',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: "You don't have access to this store",
    defaultAction: 'Go back to your home page.',
  },

  // ============ TIER 2: pre-checkout ============
  LISTING_NOT_FOUND: {
    code: 'LISTING_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'This product is no longer available',
    defaultAction: 'Remove it from your cart to continue.',
  },
  LISTING_NOT_ACTIVE: {
    code: 'LISTING_NOT_ACTIVE',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'A product is no longer for sale',
    defaultAction: 'Remove it from your cart to continue.',
  },
  CUSTOMER_NOT_FOUND: {
    code: 'CUSTOMER_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'error',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Customer record not found',
    defaultAction: 'Sign out and sign back in.',
  },
  OFFER_CANNOT_MODIFY_NON_DRAFT: {
    code: 'OFFER_CANNOT_MODIFY_NON_DRAFT',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'This offer can no longer be edited',
    defaultAction: 'Only draft offers can be changed.',
  },
  OFFER_CANNOT_ACTIVATE_NO_ITEMS: {
    code: 'OFFER_CANNOT_ACTIVATE_NO_ITEMS',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: "You can't activate an empty offer",
    defaultAction: 'Add at least one item before activating.',
  },
  OFFER_CANNOT_ACTIVATE_NO_ASSIGNMENTS: {
    code: 'OFFER_CANNOT_ACTIVATE_NO_ASSIGNMENTS',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: "You can't activate an offer with no customers",
    defaultAction: 'Assign at least one customer before activating.',
  },
  INVENTORY_OFFER_ACTIVATION_FAILED: {
    code: 'INVENTORY_OFFER_ACTIVATION_FAILED',
    httpStatus: 409,
    category: 'conflict',
    severity: 'error',
    retryable: true,
    capturePostHog: true,
    defaultMessage: "Couldn't reserve inventory for this offer",
    defaultAction: 'Try again in a moment.',
  },
  INVITATION_EXPIRED: {
    code: 'INVITATION_EXPIRED',
    httpStatus: 410,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'This invitation expired',
    defaultAction: 'Ask for a new invitation link.',
  },
  ALREADY_CUSTOMER: {
    code: 'ALREADY_CUSTOMER',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: "You're already a customer of this store",
    defaultAction: 'Sign in to continue.',
  },
  OFFER_NOT_FOUND: {
    code: 'OFFER_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Offer not found',
    defaultAction: 'Browse current offers from your store.',
  },

  // ============ TIER 3: edge cases ============
  TAKEALL_QUANTITY_MISMATCH: {
    code: 'TAKEALL_QUANTITY_MISMATCH',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Take-all offer quantities are wrong',
    defaultAction: 'Restore the offer from your cart to try again.',
  },
  ORDER_NOT_FOUND: {
    code: 'ORDER_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Order not found',
    defaultAction: 'Refresh your orders list.',
  },
  READY_TO_SHIP_FULFILLMENT_NOT_FOUND: {
    code: 'READY_TO_SHIP_FULFILLMENT_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Ready-to-ship fulfillment not found',
    defaultAction: 'Return to the Ready to Ship queue.',
  },
  SHIPMENT_DETAILS_EXPORT_INCOMPLETE: {
    code: 'SHIPMENT_DETAILS_EXPORT_INCOMPLETE',
    httpStatus: 422,
    category: 'validation',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'Shipment details are not ready to export',
    defaultAction:
      'Add the invoice, ship date, carrier, and tracking number before downloading shipment details.',
  },
  OUTBOUND_SHIPPING_ARRANGEMENT_REQUIRED: {
    code: 'OUTBOUND_SHIPPING_ARRANGEMENT_REQUIRED',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'Choose how this order will leave the warehouse',
    defaultAction: 'Select pickup, customer labels, or warehouse labels.',
  },
  LABEL_PRINT_ACKNOWLEDGEMENT_INVALID: {
    code: 'LABEL_PRINT_ACKNOWLEDGEMENT_INVALID',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: true,
    capturePostHog: false,
    defaultMessage: 'The printable label set changed',
    defaultAction: 'Print the latest labels before fulfilling this order.',
  },
  ORDER_PRINTABLE_LABELS_NOT_FOUND: {
    code: 'ORDER_PRINTABLE_LABELS_NOT_FOUND',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'This order does not have printable labels',
    defaultAction: 'Attach customer labels before printing.',
  },
  ORDER_CANNOT_DELETE_NOT_DRAFT: {
    code: 'ORDER_CANNOT_DELETE_NOT_DRAFT',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: "You can't cancel this order",
    defaultAction: 'Only draft orders can be canceled. Contact your sales rep otherwise.',
  },
  ORDER_CANNOT_DELETE_NOT_OWNER: {
    code: 'ORDER_CANNOT_DELETE_NOT_OWNER',
    httpStatus: 403,
    category: 'auth',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: "You can't cancel this order",
    defaultAction: 'Only the order creator can cancel it.',
  },
  OFFER_ITEMS_INVALID: {
    code: 'OFFER_ITEMS_INVALID',
    httpStatus: 422,
    category: 'validation',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Some items in this offer are invalid',
    defaultAction: 'Refresh the page and try again.',
  },
  BUNDLE_INTEGRITY_VIOLATION: {
    code: 'BUNDLE_INTEGRITY_VIOLATION',
    httpStatus: 422,
    category: 'validation',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Bundle allocation does not add up to the expected quantity for every size.',
    defaultAction: 'Rebalance the size run so every size reaches its needed total.',
  },
  STORE_NOT_FOUND: {
    code: 'STORE_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Store not found',
    defaultAction: 'Pick another store from your home page.',
  },
  UNAUTHORIZED: {
    code: 'UNAUTHORIZED',
    httpStatus: 403,
    category: 'auth',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: "You don't have permission",
    defaultAction: 'Contact your admin if you think this is wrong.',
  },
  UNAUTHENTICATED: {
    code: 'UNAUTHENTICATED',
    httpStatus: 401,
    category: 'auth',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'Your session expired',
    defaultAction: 'Sign in again.',
  },
  INVALID_SELECTION: {
    code: 'INVALID_SELECTION',
    httpStatus: 422,
    category: 'validation',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'Your selection is invalid',
    defaultAction: 'Adjust your choices and try again.',
  },

  // ============ Generic / fallthrough ============
  RESOURCE_NOT_FOUND: {
    code: 'RESOURCE_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: "We couldn't find what you were looking for",
    defaultAction: 'Refresh the page or go back home.',
  },
  RESOURCE_CONFLICT: {
    code: 'RESOURCE_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'That action conflicts with the current state',
    defaultAction: 'Refresh the page and try again.',
  },
  CATALOG_VARIANT_UNRESOLVED: {
    code: 'CATALOG_VARIANT_UNRESOLVED',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'A sale line does not match a live catalog variant',
    defaultAction: 'Map every unresolved SKU and size to a live product, then retry the sync.',
  },
  CATALOG_SKU_AMBIGUOUS: {
    code: 'CATALOG_SKU_AMBIGUOUS',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Multiple catalog products match a sale line',
    defaultAction: 'Map each ambiguous SKU and size to one product, then retry the sync.',
  },
  VENDOR_ORDER_LINES_EMPTY: {
    code: 'VENDOR_ORDER_LINES_EMPTY',
    httpStatus: 422,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'The vendor order contains no line items',
    defaultAction: 'Verify the vendor order data, then retry the sync.',
  },

  // ============ Reserved system codes ============
  VALIDATION_FAILED: {
    code: 'VALIDATION_FAILED',
    httpStatus: 400,
    category: 'validation',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'Some fields need attention',
    defaultAction: 'Fix the highlighted fields and try again.',
  },
  INTERNAL_ERROR: {
    code: 'INTERNAL_ERROR',
    httpStatus: 500,
    category: 'internal',
    severity: 'error',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'Something went wrong on our end',
    defaultAction:
      'Try again in a moment. If it keeps happening, contact support with this error ID.',
  },
  NETWORK_ERROR: {
    code: 'NETWORK_ERROR',
    httpStatus: 0,
    category: 'network',
    severity: 'warn',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'Could not reach the server',
    defaultAction: 'Check your internet connection and try again.',
  },
  RATE_LIMITED: {
    code: 'RATE_LIMITED',
    httpStatus: 429,
    category: 'rate_limited',
    severity: 'warn',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'Too many requests',
    defaultAction: 'Slow down and try again in a moment.',
  },
  SERVICE_BUSY: {
    code: 'SERVICE_BUSY',
    httpStatus: 503,
    category: 'internal',
    severity: 'warn',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'The system is busy right now',
    defaultAction: 'Wait a few seconds and submit again.',
  },
  AUTHORIZATION_UNAVAILABLE: {
    code: 'AUTHORIZATION_UNAVAILABLE',
    httpStatus: 503,
    category: 'auth',
    severity: 'warn',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'Authorization is temporarily unavailable',
    defaultAction: 'Try again in a moment.',
  },

  // ============ Integrity / order-number legacy bridge ============
  // All 8 legacy ORDER_NUMBER_ERROR_CODES + GENERATION_EXHAUSTED registered as integrity codes
  // so the OrderNumberError instanceof shim in errorMiddleware can wrap by lookup.
  GENERATION_EXHAUSTED: {
    code: 'GENERATION_EXHAUSTED',
    httpStatus: 500,
    category: 'integrity',
    severity: 'error',
    retryable: true,
    capturePostHog: true,
    defaultMessage: "Couldn't reserve your order number",
    defaultAction: 'Try again in a moment.',
  },
  INVALID_ORDER_TYPE: {
    code: 'INVALID_ORDER_TYPE',
    httpStatus: 500,
    category: 'integrity',
    severity: 'error',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Order type is invalid',
    defaultAction: 'Contact support if this keeps happening.',
  },
  INVALID_ORDER_ID: {
    code: 'INVALID_ORDER_ID',
    httpStatus: 500,
    category: 'integrity',
    severity: 'error',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Order ID is invalid',
    defaultAction: 'Contact support if this keeps happening.',
  },
  INVALID_FORMAT: {
    code: 'INVALID_FORMAT',
    httpStatus: 500,
    category: 'integrity',
    severity: 'error',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Order format is invalid',
    defaultAction: 'Contact support if this keeps happening.',
  },
  INVALID_CHECKSUM: {
    code: 'INVALID_CHECKSUM',
    httpStatus: 500,
    category: 'integrity',
    severity: 'error',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Order verification failed',
    defaultAction: 'Contact support if this keeps happening.',
  },
  INVALID_PREFIX: {
    code: 'INVALID_PREFIX',
    httpStatus: 500,
    category: 'integrity',
    severity: 'error',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Order prefix is invalid',
    defaultAction: 'Contact support if this keeps happening.',
  },
  CONFIGURATION_ERROR: {
    code: 'CONFIGURATION_ERROR',
    httpStatus: 500,
    category: 'integrity',
    severity: 'error',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Server is misconfigured',
    defaultAction: 'Contact support if this keeps happening.',
  },
  ENCODING_ERROR: {
    code: 'ENCODING_ERROR',
    httpStatus: 500,
    category: 'integrity',
    severity: 'error',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'Order encoding failed',
    defaultAction: 'Try again in a moment.',
  },
  DECODING_ERROR: {
    code: 'DECODING_ERROR',
    httpStatus: 500,
    category: 'integrity',
    severity: 'error',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'Order decoding failed',
    defaultAction: 'Try again in a moment.',
  },
  // ============ Org products ============
  ORG_PRODUCT_NOT_FOUND: {
    code: 'ORG_PRODUCT_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Org product not found for this organization',
    defaultAction: 'Refresh your product list.',
  },
  ORG_PRODUCT_VARIANT_OWNERSHIP: {
    code: 'ORG_PRODUCT_VARIANT_OWNERSHIP',
    httpStatus: 400,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'One or more variants do not belong to this product',
    defaultAction: 'Refresh the page and try again.',
  },
  ORG_PRODUCT_DUPLICATE_VARIANT_ID: {
    code: 'ORG_PRODUCT_DUPLICATE_VARIANT_ID',
    httpStatus: 400,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'The same variant id appears more than once in the request',
    defaultAction: 'Refresh the page and try again.',
  },
  ORG_PRODUCT_DUPLICATE_VARIANT_SIZE: {
    code: 'ORG_PRODUCT_DUPLICATE_VARIANT_SIZE',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'A variant with this size already exists on this product',
    defaultAction: 'Use a unique size for each variant.',
  },
  PRODUCT_DUPLICATE_SOURCE_NOT_FOUND: {
    code: 'PRODUCT_DUPLICATE_SOURCE_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'The product to duplicate was not found',
    defaultAction: 'Return to the product list and try again.',
  },
  PRODUCT_DUPLICATE_IDENTITY_UNCHANGED: {
    code: 'PRODUCT_DUPLICATE_IDENTITY_UNCHANGED',
    httpStatus: 422,
    category: 'validation',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'Change the product name or SKU before duplicating',
    defaultAction: 'Enter a different name, SKU, or both.',
  },
  ORG_PRODUCT_SKU_CONFLICT: {
    code: 'ORG_PRODUCT_SKU_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'An org product with this name and SKU already exists for your organization',
    defaultAction: 'Change the product name or SKU.',
  },
  ORG_PRODUCT_BARCODE_CONFLICT: {
    code: 'ORG_PRODUCT_BARCODE_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'A barcode value is already in use',
    defaultAction: 'Use a different barcode.',
  },
  FORK_BLOCKED_BY_ACTIVE_PICK: {
    code: 'FORK_BLOCKED_BY_ACTIVE_PICK',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'This product is on an active pick list and cannot be edited yet',
    defaultAction: 'Complete or cancel the active picks for this product, then try again.',
  },
  ORG_PRODUCT_FORK_CONFLICT: {
    code: 'ORG_PRODUCT_FORK_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage:
      'Your organization already customized this product; this save was based on the shared catalog version',
    defaultAction: "Reload the page to load your organization's version, then save again.",
  },
  ORG_PRODUCT_STALE_WRITE: {
    code: 'ORG_PRODUCT_STALE_WRITE',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'This product was changed by someone else since you loaded it',
    defaultAction: 'Refresh the page and try again.',
  },
  ORG_PRODUCT_REFERENCED: {
    code: 'ORG_PRODUCT_REFERENCED',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Org product cannot be deleted because it is referenced by active records',
    defaultAction: 'Remove referenced orders, units, reservations, or cart items first.',
  },
  PRODUCT_VENDOR_LINK_REFERENCED: {
    code: 'PRODUCT_VENDOR_LINK_REFERENCED',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'This vendor URL is still needed by an existing marketplace listing',
    defaultAction:
      'Delete the marketplace listing and wait for its remote deletion to finish, then remove the vendor URL.',
  },
  PRODUCT_LISTING_REFERENCED: {
    code: 'PRODUCT_LISTING_REFERENCED',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'This product is still referenced by an existing marketplace listing',
    defaultAction:
      'Delete every marketplace listing for this product and wait for remote deletion to finish, then delete the product.',
  },
  CATALOG_PRODUCT_REFERENCED: {
    code: 'CATALOG_PRODUCT_REFERENCED',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'This shared product is still the target of an organization catalog redirect',
    defaultAction:
      'Keep this product or contact support to migrate its organization catalog references.',
  },
  PRODUCT_DELETION_PENDING: {
    code: 'PRODUCT_DELETION_PENDING',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'This product is already being deleted',
    defaultAction: 'Wait for marketplace deletion to finish, then try again.',
  },
  PRODUCT_TRANSFER_SOURCE_NOT_FOUND: {
    code: 'PRODUCT_TRANSFER_SOURCE_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Shared source product not found',
    defaultAction: 'Refresh the catalog and choose another source product.',
  },
  PRODUCT_TRANSFER_TARGET_NOT_FOUND: {
    code: 'PRODUCT_TRANSFER_TARGET_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Shared transfer target not found',
    defaultAction: 'Refresh the catalog and choose another target product.',
  },
  PRODUCT_TRANSFER_IDEMPOTENCY_CONFLICT: {
    code: 'PRODUCT_TRANSFER_IDEMPOTENCY_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'This transfer request was already used for different products',
    defaultAction: 'Refresh the catalog and start the transfer again.',
  },
  PRODUCT_TRANSFER_SAME_PRODUCT: {
    code: 'PRODUCT_TRANSFER_SAME_PRODUCT',
    httpStatus: 422,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'A product cannot transfer its history to itself',
    defaultAction: 'Choose a different shared product.',
  },
  PRODUCT_TRANSFER_ARCHIVED_PRODUCT: {
    code: 'PRODUCT_TRANSFER_ARCHIVED_PRODUCT',
    httpStatus: 422,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Archived products cannot participate in a transfer',
    defaultAction: 'Choose active source and target products.',
  },
  PRODUCT_TRANSFER_STALE_WRITE: {
    code: 'PRODUCT_TRANSFER_STALE_WRITE',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'The source or target product changed during this transfer',
    defaultAction: 'Refresh the catalog, review the products, and try again.',
  },
  PRODUCT_TRANSFER_SOURCE_HAS_FORKS: {
    code: 'PRODUCT_TRANSFER_SOURCE_HAS_FORKS',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'The source product has organization forks',
    defaultAction: 'Resolve its organization forks before transferring this shared product.',
  },
  PRODUCT_TRANSFER_REQUIRES_ATOMIC_COMMAND: {
    code: 'PRODUCT_TRANSFER_REQUIRES_ATOMIC_COMMAND',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'This target has organization forks and requires the atomic transfer command',
    defaultAction: 'Use Transfer & Delete instead of transferring sales or units separately.',
  },
  PRODUCT_TRANSFER_DELETION_FAILED: {
    code: 'PRODUCT_TRANSFER_DELETION_FAILED',
    httpStatus: 409,
    category: 'conflict',
    severity: 'error',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Marketplace deletion failed after the product references were transferred',
    defaultAction: 'Review the deletion failure and repair the source product before retrying.',
  },
  PRODUCT_TRANSFER_PROMOTION_LINEAGE_UNSUPPORTED: {
    code: 'PRODUCT_TRANSFER_PROMOTION_LINEAGE_UNSUPPORTED',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Products with organization promotion lineage cannot be collapsed safely',
    defaultAction: 'Resolve the promoted organization products before transferring this product.',
  },
  PRODUCT_TRANSFER_SOURCE_EMPTY: {
    code: 'PRODUCT_TRANSFER_SOURCE_EMPTY',
    httpStatus: 422,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'The source product has no variants to transfer',
    defaultAction: 'Delete the empty product directly or choose another source.',
  },
  PRODUCT_TRANSFER_INVALID_FORK_OWNERSHIP: {
    code: 'PRODUCT_TRANSFER_INVALID_FORK_OWNERSHIP',
    httpStatus: 409,
    category: 'conflict',
    severity: 'error',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'A target product fork has invalid organization ownership',
    defaultAction: 'Contact support before retrying this transfer.',
  },
  PRODUCT_TRANSFER_INCOMPLETE_FORK_LINEAGE: {
    code: 'PRODUCT_TRANSFER_INCOMPLETE_FORK_LINEAGE',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'A target organization fork is missing variant lineage',
    defaultAction: 'Repair or recreate the organization fork before retrying.',
  },
  PRODUCT_TRANSFER_BLOCKED_BY_ACTIVE_PICK: {
    code: 'PRODUCT_TRANSFER_BLOCKED_BY_ACTIVE_PICK',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'This transfer is blocked by an active pick',
    defaultAction: 'Finish or cancel the active pick, then try again.',
  },
  PRODUCT_TRANSFER_AUDIT_ORGANIZATION_NOT_FOUND: {
    code: 'PRODUCT_TRANSFER_AUDIT_ORGANIZATION_NOT_FOUND',
    httpStatus: 409,
    category: 'conflict',
    severity: 'error',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'No organization is available to own the transfer audit',
    defaultAction: 'Contact support before retrying this transfer.',
  },
  ORG_PRODUCT_NATIVE_VARIANT_REFERENCED: {
    code: 'ORG_PRODUCT_NATIVE_VARIANT_REFERENCED',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage:
      'Cannot revert to the global catalog: this product has custom sizes not in the global catalog that are still referenced by active records',
    defaultAction:
      'Remove the orders, units, reservations, listings, or cart items on the custom sizes first, then revert.',
  },
  CATALOG_MERGE_TARGET_NOT_FOUND: {
    code: 'CATALOG_MERGE_TARGET_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Global merge target not found',
    defaultAction: 'Choose another global product and try again.',
  },
  CATALOG_MERGE_INVALID_SOURCE: {
    code: 'CATALOG_MERGE_INVALID_SOURCE',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Only a native organization product can be merged into global',
    defaultAction: 'Use Revert to global for a customized catalog product.',
  },
  CATALOG_MERGE_SKU_MISMATCH: {
    code: 'CATALOG_MERGE_SKU_MISMATCH',
    httpStatus: 422,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Source and target SKUs must match',
    defaultAction: 'Choose the global product with the same SKU.',
  },
  CATALOG_MERGE_TARGET_ARCHIVED: {
    code: 'CATALOG_MERGE_TARGET_ARCHIVED',
    httpStatus: 422,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Archived global products cannot receive a merge',
    defaultAction: 'Choose an active global product.',
  },
  ORG_PRODUCT_TRANSFER_TARGET_NOT_FOUND: {
    code: 'ORG_PRODUCT_TRANSFER_TARGET_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Transfer target not found in this organization catalog',
    defaultAction: 'Choose another visible product and try again.',
  },
  ORG_PRODUCT_TRANSFER_SAME_PRODUCT: {
    code: 'ORG_PRODUCT_TRANSFER_SAME_PRODUCT',
    httpStatus: 422,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'A product cannot transfer its history to itself',
    defaultAction: 'Choose a different product as the transfer target.',
  },
  ORG_PRODUCT_REVERT_REQUIRED: {
    code: 'ORG_PRODUCT_REVERT_REQUIRED',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Customized products must use Revert to global',
    defaultAction: 'Use the platform-admin Revert to global action.',
  },
  ORG_PRODUCT_NOT_CUSTOMIZED: {
    code: 'ORG_PRODUCT_NOT_CUSTOMIZED',
    httpStatus: 422,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Only a customized product can be reverted',
    defaultAction: 'Use Delete or Merge for a native organization product.',
  },
  ALREADY_PROMOTED: {
    code: 'ALREADY_PROMOTED',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'This org product has already been promoted to the global catalog',
    defaultAction: 'View the existing global product or refresh the page.',
  },
  SKU_EXISTS_ON_GLOBAL: {
    code: 'SKU_EXISTS_ON_GLOBAL',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'A global product with this name and SKU already exists',
    defaultAction: 'Change the product name or SKU, or link the existing global product manually.',
  },
  ORG_PRODUCT_EMPTY: {
    code: 'ORG_PRODUCT_EMPTY',
    httpStatus: 422,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Cannot promote an org product with no variants',
    defaultAction: 'Add at least one variant before promoting.',
  },
  ORG_PRODUCT_ARCHIVED: {
    code: 'ORG_PRODUCT_ARCHIVED',
    httpStatus: 422,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Cannot promote an archived org product',
    defaultAction: 'Restore the product before promoting it.',
  },
  ORG_AFFINITY_VIOLATION: {
    code: 'ORG_AFFINITY_VIOLATION',
    httpStatus: 403,
    category: 'auth',
    severity: 'error',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Resource does not belong to this organization',
    defaultAction: 'Refresh the page and try again.',
  },
  ORG_SLUG_NOT_FOUND: {
    code: 'ORG_SLUG_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Organization not found',
    defaultAction: 'Refresh the page and try again.',
  },
  ORG_RETURN_NOT_SUPPORTED: {
    code: 'ORG_RETURN_NOT_SUPPORTED',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Sale returns are not yet supported for org-scoped variants',
    defaultAction: 'Process this return manually or contact support.',
  },
  ORG_VARIANT_PVC_PAIR_REQUIRED: {
    code: 'ORG_VARIANT_PVC_PAIR_REQUIRED',
    httpStatus: 400,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Org variant updates must include the paired orgVcid',
    defaultAction: 'Refresh the page and try again.',
  },
  CUSTOMER_NOT_IN_ORGANIZATION: {
    code: 'CUSTOMER_NOT_IN_ORGANIZATION',
    httpStatus: 400,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Customer does not exist in this organization',
    defaultAction: 'Pick a customer from your organization and try again.',
  },
  ORDER_ASSIGNEE_NOT_MEMBER: {
    code: 'ORDER_ASSIGNEE_NOT_MEMBER',
    httpStatus: 422,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Assignee is not a member of this organization',
    defaultAction: 'Pick a member of this organization and try again.',
  },
  SUPPLIER_NOT_IN_ORGANIZATION: {
    code: 'SUPPLIER_NOT_IN_ORGANIZATION',
    httpStatus: 400,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Supplier does not exist in this organization',
    defaultAction: 'Pick a supplier from your organization and try again.',
  },
  ORDER_ITEM_SOURCE_REQUIRED: {
    code: 'ORDER_ITEM_SOURCE_REQUIRED',
    httpStatus: 400,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage:
      'An order item must reference either a global product variant or an org variant condition',
    defaultAction: 'Refresh the page and try again.',
  },
  AUTOMATION_NOT_AVAILABLE_FOR_ORG: {
    code: 'AUTOMATION_NOT_AVAILABLE_FOR_ORG',
    httpStatus: 422,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage:
      'Automated repricing and quantity sync are not available for org-source listings',
    defaultAction: 'Org-source listings sell on your own store with no external sync.',
  },
  ORG_PRODUCT_IMAGE_NOT_FOUND: {
    code: 'ORG_PRODUCT_IMAGE_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Image not found for this org product',
    defaultAction: 'Refresh the page and try again.',
  },
  ORG_PRODUCT_IMAGE_UPLOAD_INVALID: {
    code: 'ORG_PRODUCT_IMAGE_UPLOAD_INVALID',
    httpStatus: 400,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Image upload payload is invalid',
    defaultAction: 'Pick a supported image file and try again.',
  },
  ORG_PRODUCT_IMAGE_REORDER_INVALID: {
    code: 'ORG_PRODUCT_IMAGE_REORDER_INVALID',
    httpStatus: 400,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Image reorder payload is invalid',
    defaultAction: 'Reload the gallery and try again.',
  },
  ORG_PRODUCT_IMAGE_UPLOAD_IN_PROGRESS: {
    code: 'ORG_PRODUCT_IMAGE_UPLOAD_IN_PROGRESS',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'An identical image upload is still being finalized',
    defaultAction: 'Wait a moment and retry the upload.',
  },
  ORG_PRODUCT_IMAGE_UPLOAD_LIMIT: {
    code: 'ORG_PRODUCT_IMAGE_UPLOAD_LIMIT',
    httpStatus: 413,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Upload batch exceeds the allowed size',
    defaultAction: 'Upload fewer or smaller files per request.',
  },
  ORG_PRODUCT_VARIANT_CONDITION_NOT_FOUND: {
    code: 'ORG_PRODUCT_VARIANT_CONDITION_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Org product variant condition not found',
    defaultAction: 'Refresh and try again.',
  },
  ORG_PRODUCT_VENDOR_PRODUCT_NOT_FOUND: {
    code: 'ORG_PRODUCT_VENDOR_PRODUCT_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Vendor mapping not found on this org product',
    defaultAction: 'Refresh the page and try again.',
  },
  ORG_PRODUCT_VENDOR_NOT_FOUND: {
    code: 'ORG_PRODUCT_VENDOR_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Vendor not found',
    defaultAction: 'Pick a valid vendor and try again.',
  },
  ORG_PRODUCT_VENDOR_URL_CONFLICT: {
    code: 'ORG_PRODUCT_VENDOR_URL_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Another product is already mapped to this vendor URL',
    defaultAction: 'Pick a different URL or unlink the existing mapping.',
  },
  ORG_PRODUCT_VENDOR_VARIANT_CONFLICT: {
    code: 'ORG_PRODUCT_VENDOR_VARIANT_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Another product is already paired to this marketplace variant',
    defaultAction: 'Unpair the other product first, or pair this one to a different variant.',
  },
  // Generic data integrity invariant — cross-cutting, used by polymorphic
  // source resolvers (variant-source.ts) for any table with vcid/orgVcid or
  // productVariantId/orgProductVariantId pairs.
  INVARIANT_VIOLATION: {
    code: 'INVARIANT_VIOLATION',
    httpStatus: 500,
    category: 'integrity',
    severity: 'error',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Data integrity invariant violated',
    defaultAction: 'Contact support if this keeps happening.',
  },
  SHIPMENT_NOT_FOUND: {
    code: 'SHIPMENT_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Shipment not found',
    defaultAction: 'Refresh the order and try again.',
  },
  SHIPMENT_ORDER_NOT_FOUND: {
    code: 'SHIPMENT_ORDER_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Order not found for this shipment',
    defaultAction: 'Refresh the order and try again.',
  },
  SHIPMENT_VERSION_CONFLICT: {
    code: 'SHIPMENT_VERSION_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: true,
    capturePostHog: false,
    defaultMessage: 'Shipment changed while you were editing it',
    defaultAction: 'Review the latest shipment and try again.',
  },
  FULFILLMENT_IDEMPOTENCY_CONFLICT: {
    code: 'FULFILLMENT_IDEMPOTENCY_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Fulfillment request key was already used for different details',
    defaultAction: 'Start a new fulfillment request.',
  },
  FULFILLMENT_QUANTITY_EXCEEDED: {
    code: 'FULFILLMENT_QUANTITY_EXCEEDED',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: true,
    capturePostHog: false,
    defaultMessage: 'Some items were fulfilled in another session',
    defaultAction: 'Review the remaining quantities and try again.',
  },
  FULFILLMENT_ORDER_STATE_CONFLICT: {
    code: 'FULFILLMENT_ORDER_STATE_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'This order can no longer be fulfilled',
    defaultAction: 'Return to open orders and review its current status.',
  },
  SHIPMENT_IDEMPOTENCY_CONFLICT: {
    code: 'SHIPMENT_IDEMPOTENCY_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Shipment request key was already used for different details',
    defaultAction: 'Start a new shipment request.',
  },
  SHIPMENT_INVALID_TRANSITION: {
    code: 'SHIPMENT_INVALID_TRANSITION',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'Shipment cannot move to the requested status',
    defaultAction: 'Refresh the shipment and choose an available action.',
  },
  SHIPPING_FULFILLMENT_INVALID: {
    code: 'SHIPPING_FULFILLMENT_INVALID',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Shipment must target a packed fulfillment from this order',
    defaultAction: 'Refresh the ready-to-ship queue and try again.',
  },
  SHIPPING_FULFILLMENT_VERSION_CONFLICT: {
    code: 'SHIPPING_FULFILLMENT_VERSION_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: true,
    capturePostHog: false,
    defaultMessage: 'Packed fulfillment changed before dispatch',
    defaultAction: 'Review the latest fulfillment and try again.',
  },
  SHIPPING_ORIGIN_LOCATION_INVALID: {
    code: 'SHIPPING_ORIGIN_LOCATION_INVALID',
    httpStatus: 422,
    category: 'validation',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'The selected warehouse does not have a complete shipping address',
    defaultAction: 'Complete the warehouse address or enter a manual ship-from address.',
  },
  SHIPPING_LOCATION_NOT_FOUND: {
    code: 'SHIPPING_LOCATION_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Warehouse location not found',
    defaultAction: 'Select an active warehouse and try again.',
  },
  SHIPPING_DISPATCH_CONFLICT: {
    code: 'SHIPPING_DISPATCH_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Fulfillment was already dispatched by another request',
    defaultAction: 'Refresh the shipment before continuing.',
  },
  SHIPPING_PACKAGE_IDEMPOTENCY_CONFLICT: {
    code: 'SHIPPING_PACKAGE_IDEMPOTENCY_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Package request key was already used for different details',
    defaultAction: 'Start a new package request.',
  },
  SHIPPING_PACKAGE_ASSIGNMENT_INVALID: {
    code: 'SHIPPING_PACKAGE_ASSIGNMENT_INVALID',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'One or more units do not belong to this packed fulfillment',
    defaultAction: 'Refresh the packed units and review the package assignments.',
  },
  SHIPPING_PACKAGE_NOT_FOUND: {
    code: 'SHIPPING_PACKAGE_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Package not found',
    defaultAction: 'Refresh the shipment and try again.',
  },
  SHIPPING_PACKAGE_VERSION_CONFLICT: {
    code: 'SHIPPING_PACKAGE_VERSION_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: true,
    capturePostHog: false,
    defaultMessage: 'Package changed while you were editing it',
    defaultAction: 'Review the latest package and try again.',
  },
  SHIPMENT_LABEL_NOT_FOUND: {
    code: 'SHIPMENT_LABEL_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Shipment label not found',
    defaultAction: 'Refresh the shipment and try again.',
  },
  SHIPMENT_LABEL_VERSION_CONFLICT: {
    code: 'SHIPMENT_LABEL_VERSION_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: true,
    capturePostHog: false,
    defaultMessage: 'Shipment label changed while you were editing it',
    defaultAction: 'Review the latest label and try again.',
  },
  SHIPPING_PACKAGE_REMOVE_CONFLICT: {
    code: 'SHIPPING_PACKAGE_REMOVE_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Package was already removed by a different request',
    defaultAction: 'Refresh the shipment before continuing.',
  },
  SHIPPING_PACKAGE_MEASUREMENT_INVALID: {
    code: 'SHIPPING_PACKAGE_MEASUREMENT_INVALID',
    httpStatus: 422,
    category: 'validation',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'A package measurement is outside the supported range',
    defaultAction: 'Correct the highlighted package measurement and try again.',
  },
  SHIPMENT_RATE_DETAILS_INCOMPLETE: {
    code: 'SHIPMENT_RATE_DETAILS_INCOMPLETE',
    httpStatus: 422,
    category: 'validation',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'Shipment details are incomplete for rate comparison',
    defaultAction: 'Add the ship date, addresses, package weights, and dimensions.',
  },
  SHIPPING_ESTIMATE_ORDER_INELIGIBLE: {
    code: 'SHIPPING_ESTIMATE_ORDER_INELIGIBLE',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'This sales order is not eligible for a shipping estimate',
    defaultAction: 'Use a saved customer sales order that has not shipped or been completed.',
  },
  SHIPPING_ESTIMATE_INPUT_STALE: {
    code: 'SHIPPING_ESTIMATE_INPUT_STALE',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: true,
    capturePostHog: false,
    defaultMessage: 'The sales order changed after this estimate was prepared',
    defaultAction: 'Regenerate the package estimate from the latest saved order.',
  },
  SHIPPING_ESTIMATE_ORIGIN_REQUIRED: {
    code: 'SHIPPING_ESTIMATE_ORIGIN_REQUIRED',
    httpStatus: 422,
    category: 'validation',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'Every estimated package group needs a ship-from warehouse',
    defaultAction: 'Choose a warehouse for the unassigned packages.',
  },
  SHIPPING_ESTIMATE_PACKAGES_INVALID: {
    code: 'SHIPPING_ESTIMATE_PACKAGES_INVALID',
    httpStatus: 422,
    category: 'validation',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'One or more estimated packages are invalid',
    defaultAction: 'Correct the package weights and dimensions before requesting rates.',
  },
  SHIPPING_RATE_REQUEST_REJECTED: {
    code: 'SHIPPING_RATE_REQUEST_REJECTED',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'No shipping rates are available for these shipment details',
    defaultAction: 'Review the addresses and packages or choose another carrier.',
  },
  SHIPPING_RATE_LIMITED: {
    code: 'SHIPPING_RATE_LIMITED',
    httpStatus: 429,
    category: 'external_service',
    severity: 'warn',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'Shipping rates are temporarily limited',
    defaultAction: 'Wait a moment and compare rates again.',
  },
  SHIPPING_RATE_PROVIDER_RESPONSE_INVALID: {
    code: 'SHIPPING_RATE_PROVIDER_RESPONSE_INVALID',
    httpStatus: 502,
    category: 'external_service',
    severity: 'error',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'The shipping provider returned an invalid rate response',
    defaultAction: 'Try again. Contact support if the problem continues.',
  },
  SHIPPING_RATE_QUOTE_INVALID: {
    code: 'SHIPPING_RATE_QUOTE_INVALID',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: true,
    capturePostHog: false,
    defaultMessage: 'The selected shipping rate is no longer valid',
    defaultAction: 'Compare rates again before purchasing labels.',
  },
  SHIPPING_LABEL_OPERATION_NOT_FOUND: {
    code: 'SHIPPING_LABEL_OPERATION_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Label operation not found',
    defaultAction: 'Refresh the shipment and try again.',
  },
  SHIPPING_LABEL_NOT_FOUND: {
    code: 'SHIPPING_LABEL_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Shipping label not found',
    defaultAction: 'Refresh the shipment and review its active label.',
  },
  SHIPPING_LABEL_IDEMPOTENCY_CONFLICT: {
    code: 'SHIPPING_LABEL_IDEMPOTENCY_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Label request key was already used for different details',
    defaultAction: 'Start a new label request.',
  },
  SHIPPING_LABEL_UPLOAD_NOT_FOUND: {
    code: 'SHIPPING_LABEL_UPLOAD_NOT_FOUND',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: true,
    capturePostHog: false,
    defaultMessage: 'Uploaded label bytes were not found',
    defaultAction: 'Retry the label upload.',
  },
  SHIPPING_LABEL_UPLOAD_CONTENT_ID_MISMATCH: {
    code: 'SHIPPING_LABEL_UPLOAD_CONTENT_ID_MISMATCH',
    httpStatus: 400,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Uploaded label content did not match the upload request',
    defaultAction: 'Select the label file again and retry.',
  },
  SHIPPING_LABEL_PURCHASE_CONFLICT: {
    code: 'SHIPPING_LABEL_PURCHASE_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'A label purchase already exists for this package version',
    defaultAction: 'Refresh the shipment and resume the existing label operation.',
  },
  SHIPPING_LABEL_PACKAGE_INVALID: {
    code: 'SHIPPING_LABEL_PACKAGE_INVALID',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'One or more packages cannot be used for this label purchase',
    defaultAction: 'Refresh the shipment and review the package labels and versions.',
  },
  SHIPPING_DISPATCH_NOT_READY: {
    code: 'SHIPPING_DISPATCH_NOT_READY',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'This shipment is not ready to complete',
    defaultAction: 'Resolve the listed shipment requirements and try again.',
  },
  SHIPPING_LABEL_OPERATION_IN_PROGRESS: {
    code: 'SHIPPING_LABEL_OPERATION_IN_PROGRESS',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: true,
    capturePostHog: false,
    defaultMessage: 'This label operation is already in progress',
    defaultAction: 'Wait for the current attempt to finish.',
  },
  SHIPPING_LABEL_OPERATION_INVALID_STATE: {
    code: 'SHIPPING_LABEL_OPERATION_INVALID_STATE',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'This label operation cannot run from its current state',
    defaultAction: 'Refresh the shipment and review the operation status.',
  },
  SHIPPING_LABEL_FINALIZATION_FAILED: {
    code: 'SHIPPING_LABEL_FINALIZATION_FAILED',
    httpStatus: 502,
    category: 'external_service',
    severity: 'error',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'The purchased labels could not be saved',
    defaultAction: 'Resume the label operation. You will not be charged again.',
  },
  SHIPPING_LABEL_NOT_VOIDABLE: {
    code: 'SHIPPING_LABEL_NOT_VOIDABLE',
    httpStatus: 409,
    category: 'external_service',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'The shipping provider did not approve this label void',
    defaultAction: 'Refresh the label status or contact the carrier account owner.',
  },
  SHIPPING_LABEL_VOID_CONFLICT: {
    code: 'SHIPPING_LABEL_VOID_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'This uploaded label was already removed by another request',
    defaultAction: 'Refresh the shipment before uploading a replacement label.',
  },
  SHIPMENT_COMPLETION_VOID_FORBIDDEN: {
    code: 'SHIPMENT_COMPLETION_VOID_FORBIDDEN',
    httpStatus: 403,
    category: 'auth',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'You are not authorized to void this shipment completion',
    defaultAction: 'Ask an authorized warehouse lead, admin, or owner to complete the correction.',
  },
  SHIPMENT_COMPLETION_VOID_INVALID_STATE: {
    code: 'SHIPMENT_COMPLETION_VOID_INVALID_STATE',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'This shipment completion can no longer be voided',
    defaultAction: 'Refresh the shipment and review its current state.',
  },
  SHIPMENT_COMPLETION_VOID_VERSION_CONFLICT: {
    code: 'SHIPMENT_COMPLETION_VOID_VERSION_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: true,
    capturePostHog: false,
    defaultMessage: 'The shipment completion changed while you were editing it',
    defaultAction: 'Refresh the shipment and reconcile the latest correction state.',
  },
  SHIPMENT_COMPLETION_VOID_IN_PROGRESS: {
    code: 'SHIPMENT_COMPLETION_VOID_IN_PROGRESS',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: true,
    capturePostHog: false,
    defaultMessage: 'A shipment completion correction is already in progress',
    defaultAction: 'Open the active correction to review its progress.',
  },
  SHIPMENT_COMPLETION_VOID_IDEMPOTENCY_CONFLICT: {
    code: 'SHIPMENT_COMPLETION_VOID_IDEMPOTENCY_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'This correction key was already used for different details',
    defaultAction: 'Generate a new key for the new correction request.',
  },
  SHIPMENT_COMPLETION_VOID_MANUAL_CONFIRMATION_REQUIRED: {
    code: 'SHIPMENT_COMPLETION_VOID_MANUAL_CONFIRMATION_REQUIRED',
    httpStatus: 400,
    category: 'validation',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'Carrier confirmation and a manual-resolution note are required',
    defaultAction: 'Confirm the carrier was checked and provide the required note.',
  },
  SHIPPING_EXTERNAL_PACKAGE_CONFLICT: {
    code: 'SHIPPING_EXTERNAL_PACKAGE_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'The inbound packages changed during this submission',
    defaultAction: 'Refresh the shipment and submit the remaining tracking numbers.',
  },
  SHIPPING_TRACKING_NUMBER_INVALID: {
    code: 'SHIPPING_TRACKING_NUMBER_INVALID',
    httpStatus: 422,
    category: 'validation',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'Enter a valid tracking number',
    defaultAction: 'Scan or enter a tracking number with letters or numbers.',
  },
  SHIPPING_TRACKING_NUMBER_CONFLICT: {
    code: 'SHIPPING_TRACKING_NUMBER_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'This tracking number is already active on the purchase order',
    defaultAction: 'Refresh the shipment and use a different tracking number.',
  },
  SHIPPING_VENDOR_LABEL_EVIDENCE_CONFLICT: {
    code: 'SHIPPING_VENDOR_LABEL_EVIDENCE_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Vendor label evidence does not match the existing label',
    defaultAction: 'Review the vendor label and tracking identity before retrying.',
  },
  SHIPPING_VENDOR_LABEL_EVIDENCE_INVALID: {
    code: 'SHIPPING_VENDOR_LABEL_EVIDENCE_INVALID',
    httpStatus: 422,
    category: 'validation',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'Vendor label evidence is incomplete',
    defaultAction: 'Provide a document or a complete carrier and tracking number.',
  },
  SHIPPING_VENDOR_LABEL_ORDER_NOT_FOUND: {
    code: 'SHIPPING_VENDOR_LABEL_ORDER_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'The vendor order for this label was not found',
    defaultAction: 'Sync the vendor order before importing its label.',
  },
  SHIPPING_VENDOR_LABEL_STORAGE_FAILURE: {
    code: 'SHIPPING_VENDOR_LABEL_STORAGE_FAILURE',
    httpStatus: 503,
    category: 'external_service',
    severity: 'error',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'The vendor label document could not be stored',
    defaultAction: 'Retry the vendor label sync in a moment.',
  },
  SHIPPING_VENDOR_LABEL_DOCUMENT_REJECTED: {
    code: 'SHIPPING_VENDOR_LABEL_DOCUMENT_REJECTED',
    httpStatus: 422,
    category: 'external_service',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'The vendor label document was rejected',
    defaultAction: 'Refresh the vendor label evidence before retrying.',
  },
  SHIPPING_VENDOR_LABEL_INFRASTRUCTURE_FAILURE: {
    code: 'SHIPPING_VENDOR_LABEL_INFRASTRUCTURE_FAILURE',
    httpStatus: 503,
    category: 'internal',
    severity: 'error',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'Vendor label processing is temporarily unavailable',
    defaultAction: 'Retry the vendor label sync in a moment.',
  },
  SHIPPING_TRACKING_CONNECTION_UNAVAILABLE: {
    code: 'SHIPPING_TRACKING_CONNECTION_UNAVAILABLE',
    httpStatus: 409,
    category: 'business_rule',
    severity: 'info',
    retryable: true,
    capturePostHog: false,
    defaultMessage: 'No tracking connection is available for this carrier',
    defaultAction: 'Connect a supported tracking provider or retry later.',
  },
  SHIPPING_TRACKING_SUBSCRIPTION_NOT_FOUND: {
    code: 'SHIPPING_TRACKING_SUBSCRIPTION_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'Tracking subscription not found',
    defaultAction: 'Register this package for tracking and try again.',
  },
  SHIPPING_TRACKING_PACKAGE_INVALID: {
    code: 'SHIPPING_TRACKING_PACKAGE_INVALID',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Package cannot be registered for tracking',
    defaultAction: 'Add a valid tracking number to an active carrier package.',
  },
  SHIPPING_TRACKING_CARRIER_UNSUPPORTED: {
    code: 'SHIPPING_TRACKING_CARRIER_UNSUPPORTED',
    httpStatus: 422,
    category: 'validation',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'Choose a supported carrier for tracking updates',
    defaultAction: 'Select the exact carrier service and try again.',
  },
  SHIPPING_TRACKING_IDENTITY_CONFLICT: {
    code: 'SHIPPING_TRACKING_IDENTITY_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Tracking update does not match this package',
    defaultAction: 'Refresh the package tracking registration.',
  },
  SHIPPING_TRACKING_PROVIDER_RESPONSE_INVALID: {
    code: 'SHIPPING_TRACKING_PROVIDER_RESPONSE_INVALID',
    httpStatus: 502,
    category: 'external_service',
    severity: 'error',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'Tracking provider returned invalid data',
    defaultAction: 'Try tracking reconciliation again.',
  },
  SHIPPING_TRACKING_APPLICATION_CONFLICT: {
    code: 'SHIPPING_TRACKING_APPLICATION_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: true,
    capturePostHog: false,
    defaultMessage: 'Package tracking changed during reconciliation',
    defaultAction: 'Retry tracking reconciliation with the latest package.',
  },
  SHIPPING_TRACKING_INFRASTRUCTURE_FAILURE: {
    code: 'SHIPPING_TRACKING_INFRASTRUCTURE_FAILURE',
    httpStatus: 503,
    category: 'internal',
    severity: 'error',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'Tracking processing is temporarily unavailable',
    defaultAction: 'Try tracking reconciliation again in a moment.',
  },
  SHIPPING_WEBHOOK_UNAUTHORIZED: {
    code: 'SHIPPING_WEBHOOK_UNAUTHORIZED',
    httpStatus: 401,
    category: 'auth',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Shipping webhook signature is invalid',
    defaultAction: 'Retry the automatic provider webhook setup.',
  },
  SHIPPING_WEBHOOK_NOT_FOUND: {
    code: 'SHIPPING_WEBHOOK_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'info',
    retryable: false,
    capturePostHog: false,
    defaultMessage: 'Shipping webhook endpoint not found',
    defaultAction: 'Verify the provider webhook URL.',
  },
  SHIPPING_WEBHOOK_INVALID: {
    code: 'SHIPPING_WEBHOOK_INVALID',
    httpStatus: 400,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Shipping webhook body is invalid',
    defaultAction: 'Verify the provider webhook configuration.',
  },
  SHIPPING_WEBHOOK_TOO_LARGE: {
    code: 'SHIPPING_WEBHOOK_TOO_LARGE',
    httpStatus: 413,
    category: 'validation',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Shipping webhook body is too large',
    defaultAction: 'Reduce the webhook payload size.',
  },
  SHIPPING_WEBHOOK_REPLAY_CONFLICT: {
    code: 'SHIPPING_WEBHOOK_REPLAY_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'error',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Shipping webhook event identity was reused',
    defaultAction: 'Inspect the provider webhook replay.',
  },
  SHIPPING_CONNECTION_NOT_FOUND: {
    code: 'SHIPPING_CONNECTION_NOT_FOUND',
    httpStatus: 404,
    category: 'not_found',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Shipping connection not found',
    defaultAction: 'Configure the shipping integration and try again.',
  },
  SHIPPING_CONNECTION_CONFLICT: {
    code: 'SHIPPING_CONNECTION_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Shipping connection request conflicts with existing state',
    defaultAction: 'Refresh the integration and start a new request.',
  },
  SHIPPING_CONNECTION_VERSION_CONFLICT: {
    code: 'SHIPPING_CONNECTION_VERSION_CONFLICT',
    httpStatus: 409,
    category: 'conflict',
    severity: 'info',
    retryable: true,
    capturePostHog: false,
    defaultMessage: 'Shipping connection changed while you were editing it',
    defaultAction: 'Review the latest connection and try again.',
  },
  SHIPPING_CONNECTION_INACTIVE: {
    code: 'SHIPPING_CONNECTION_INACTIVE',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Shipping connection is not active',
    defaultAction: 'Enable or reconnect the shipping integration.',
  },
  SHIPPING_CONNECTION_CAPABILITY_UNAVAILABLE: {
    code: 'SHIPPING_CONNECTION_CAPABILITY_UNAVAILABLE',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'warn',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Shipping capability is not enabled for this connection',
    defaultAction: 'Update the connection capabilities or choose another provider.',
  },
  SHIPPING_CONNECTION_PROVIDER_UNAVAILABLE: {
    code: 'SHIPPING_CONNECTION_PROVIDER_UNAVAILABLE',
    httpStatus: 503,
    category: 'external_service',
    severity: 'error',
    retryable: true,
    capturePostHog: true,
    defaultMessage: 'Shipping provider is temporarily unavailable',
    defaultAction: 'Try again in a moment.',
  },
  SHIPPING_CONNECTION_CREDENTIAL_INVALID: {
    code: 'SHIPPING_CONNECTION_CREDENTIAL_INVALID',
    httpStatus: 422,
    category: 'business_rule',
    severity: 'error',
    retryable: false,
    capturePostHog: true,
    defaultMessage: 'Shipping connection credentials could not be loaded',
    defaultAction: 'Rotate or reconnect the shipping integration.',
  },
} as const satisfies Record<string, ErrorRegistryEntry>;

export type ErrorCode = keyof typeof ERROR_REGISTRY;

export function isErrorCode(value: string): value is ErrorCode {
  return value in ERROR_REGISTRY;
}

export function getErrorEntry(code: ErrorCode): ErrorRegistryEntry {
  return ERROR_REGISTRY[code];
}
